import React, { useState, useEffect } from 'react';
import ZohoApiConfigCard from './ZohoApiConfigCard';
import ZohoMappingsCard from './ZohoMappingsCard';
import ZohoSyncQueueCard from './ZohoSyncQueueCard';
import zohoService from '../../../services/zohoService';
import 'react-toastify/dist/ReactToastify.css';

const ZohoForm = ({
  initialData,
  onSave,
  isLoading,
  onRefresh,
  dataCenterOptions = [],
}) => {
  const [formData, setFormData] = useState({
    zoho_client_id: '',
    zoho_client_secret: '',
    zoho_redirect_uri: '',
    zoho_data_center: { id: 'IN', label: 'India (IN)' },
    zoho_status: { id: '1', label: 'Active' },
    zoho_tenant: null,
    expires: '',
    is_connected: false,
  });

  const [accountMappings, setAccountMappings] = useState({});
  const [mappingDefinitions, setMappingDefinitions] = useState(null);
  const [errors, setErrors] = useState({});

  // Fetch account mapping definitions from the backend
  useEffect(() => {
    const fetchDefinitions = async () => {
      try {
        const defs = await zohoService.getAccountMappingDefinitions();
        setMappingDefinitions(defs);
      } catch (error) {
        // Definitions endpoint may fail if not connected; that's OK
        setMappingDefinitions(null);
      }
    };
    fetchDefinitions();
  }, []);

  useEffect(() => {
    if (initialData) {
      const dcId = initialData.zoho_data_center || 'IN';
      const dcOption = dataCenterOptions.find((o) => o.id === dcId)
        || { id: dcId, label: dcId };

      setFormData({
        zoho_client_id: initialData.zoho_client_id || '',
        zoho_client_secret: initialData.zoho_client_secret || '',
        zoho_redirect_uri: initialData.zoho_redirect_uri || '',
        zoho_data_center: dcOption,
        zoho_status: initialData.zoho_status || { id: '1', label: 'Active' },
        zoho_tenant: initialData.zoho_tenant || null,
        expires: initialData.expires || '',
        is_connected: initialData.is_connected || false,
      });

      // Build account mappings dynamically from definitions + saved values
      if (mappingDefinitions?.sections) {
        const mappings = {};
        mappingDefinitions.sections.forEach((section) => {
          section.fields.forEach((field) => {
            mappings[field.key] = initialData[field.key] || null;
          });
        });
        setAccountMappings(mappings);
      } else {
        // Fallback: use whatever keys are present in initialData
        const knownKeys = [
          'zoho_raw_materials_account',
          'zoho_finished_goods_inventory_account',
          'zoho_vat_input_account',
          'zoho_goods_in_transit_account',
          'zoho_wip_account',
          'zoho_inventory_adjustment_account',
          'zoho_cost_of_tailoring_account',
          'zoho_tailors_wages_account',
          'zoho_cogs_account',
          'zoho_cash_account',
          'zoho_customer_advance_account',
          'zoho_sales_lpo_account',
          'zoho_sales_walkin_account',
        ];
        const mappings = {};
        knownKeys.forEach((key) => {
          mappings[key] = initialData[key] || null;
        });
        setAccountMappings(mappings);
      }

    }
  }, [initialData, dataCenterOptions, mappingDefinitions]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page header — compact */}
      <div className="mb-3">
        <h4 className="mb-0">Zoho Settings</h4>
        <p className="text-muted small mb-0">
          Configure Zoho Books integration for TailorJet.
        </p>
      </div>

      {/* Card 1: API Config + Connection status (merged) */}
      <ZohoApiConfigCard
        formData={formData}
        setFormData={setFormData}
        dataCenterOptions={dataCenterOptions}
        errors={errors}
        setErrors={setErrors}
        onSaveCredentials={onSave}
        onRefresh={onRefresh}
        isLoading={isLoading}
        isConnected={formData.is_connected}
        expires={formData.expires}
      />

      {/* Card 2: Organization + Account Mapping */}
      <ZohoMappingsCard
        isConnected={formData.is_connected}
        zohoTenant={formData.zoho_tenant}
        setZohoTenant={(value) => setFormData((prev) => ({ ...prev, zoho_tenant: value }))}
        accountMappings={accountMappings}
        setAccountMappings={setAccountMappings}
        mappingDefinitions={mappingDefinitions}
        onRefresh={onRefresh}
      />

      {formData.is_connected && formData.zoho_tenant && (
        <ZohoSyncQueueCard />
      )}
    </div>
  );
};

export default ZohoForm;
